import React, { useEffect, useState } from 'react'
import { PageHeader } from '@/components/ui/PageHeader'
import { AlertCircle, CheckCircle } from 'lucide-react'
import {
  TapRateCalculatorSettings,
  listRateCalculatorSettings,
  updateRateCalculatorSettings,
  createRateCalculatorSettings,
  activateRateCalculatorSettings,
} from '@/api/tapResources'
import { formatDateTime } from '@/utils/dateFormat'

// Feature #229 — TAP Rate Calculator settings sub-page.
//
// Drives the Assessment tab's live rate calculator. Exactly one row is
// "active" at any time; activating a row deactivates the others. New rows
// are created inactive so finance can prepare next FY's record before
// flipping the switch. All percentage inputs are entered as percent
// (e.g. 12 for 12%) and converted to/from decimal at the network boundary.

interface Props {
  embedded?: boolean
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  textTransform: 'uppercase',
  marginBottom: '4px',
}

const sectionStyle: React.CSSProperties = {
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '16px 20px',
  marginBottom: '16px',
  backgroundColor: 'var(--color-white)',
}

const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}
const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'var(--color-white)',
  color: 'var(--color-black)',
  border: '1px solid var(--color-light-grey)',
}

// Convert decimal (0.12) ↔ percent (12) for input fields. Kept tiny so the
// JSX stays readable.
const dec2pct = (d: number | undefined): string => (d == null ? '' : (d * 100).toString())
const pct2dec = (s: string): number => Number(s) / 100

interface FormState {
  fyLabel: string
  notes: string
  superRatePct: string
  payrollTaxRatePct: string
  hoursPerDay: string
  weekdaysPerYear: string
  annualLeaveDays: string
  sickLeaveDays: string
  professionalDevelopmentDays: string
  internalManagementDays: string
  utilisationTargetPct: string
  minTgpFullTimePct: string
  minSgpFullTimePct: string
  minTgpPartTimePct: string
  minSgpPartTimePct: string
  minTgpCasualPct: string
  minSgpCasualPct: string
  minTgpContractorPct: string
  minSgpContractorPct: string
  defaultBillableDaysCasual: string
}

const blankForm = (): FormState => ({
  fyLabel: '',
  notes: '',
  superRatePct: '12',
  payrollTaxRatePct: '2.5',
  hoursPerDay: '7.5',
  weekdaysPerYear: '251',
  annualLeaveDays: '20',
  sickLeaveDays: '10',
  professionalDevelopmentDays: '14',
  internalManagementDays: '10',
  utilisationTargetPct: '90',
  minTgpFullTimePct: '45',
  minSgpFullTimePct: '20',
  minTgpPartTimePct: '45',
  minSgpPartTimePct: '20',
  minTgpCasualPct: '50',
  minSgpCasualPct: '20',
  minTgpContractorPct: '36',
  minSgpContractorPct: '20',
  defaultBillableDaysCasual: '251',
})

const fromSettings = (s: TapRateCalculatorSettings): FormState => ({
  fyLabel: s.fyLabel,
  notes: s.notes || '',
  superRatePct: dec2pct(s.superRate),
  payrollTaxRatePct: dec2pct(s.payrollTaxRate),
  hoursPerDay: s.hoursPerDay.toString(),
  weekdaysPerYear: s.weekdaysPerYear.toString(),
  annualLeaveDays: s.annualLeaveDays.toString(),
  sickLeaveDays: s.sickLeaveDays.toString(),
  professionalDevelopmentDays: s.professionalDevelopmentDays.toString(),
  internalManagementDays: s.internalManagementDays.toString(),
  utilisationTargetPct: dec2pct(s.utilisationTargetPct),
  minTgpFullTimePct: dec2pct(s.minTgpFullTime),
  minSgpFullTimePct: dec2pct(s.minSgpFullTime),
  minTgpPartTimePct: dec2pct(s.minTgpPartTime),
  minSgpPartTimePct: dec2pct(s.minSgpPartTime),
  minTgpCasualPct: dec2pct(s.minTgpCasual),
  minSgpCasualPct: dec2pct(s.minSgpCasual),
  minTgpContractorPct: dec2pct(s.minTgpContractor),
  minSgpContractorPct: dec2pct(s.minSgpContractor),
  defaultBillableDaysCasual: s.defaultBillableDaysCasual.toString(),
})

const toPayload = (f: FormState): Partial<TapRateCalculatorSettings> => ({
  fyLabel: f.fyLabel.trim(),
  notes: f.notes.trim() || undefined,
  superRate: pct2dec(f.superRatePct),
  payrollTaxRate: pct2dec(f.payrollTaxRatePct),
  hoursPerDay: Number(f.hoursPerDay),
  weekdaysPerYear: Number(f.weekdaysPerYear),
  annualLeaveDays: Number(f.annualLeaveDays),
  sickLeaveDays: Number(f.sickLeaveDays),
  professionalDevelopmentDays: Number(f.professionalDevelopmentDays),
  internalManagementDays: Number(f.internalManagementDays),
  utilisationTargetPct: pct2dec(f.utilisationTargetPct),
  minTgpFullTime: pct2dec(f.minTgpFullTimePct),
  minSgpFullTime: pct2dec(f.minSgpFullTimePct),
  minTgpPartTime: pct2dec(f.minTgpPartTimePct),
  minSgpPartTime: pct2dec(f.minSgpPartTimePct),
  minTgpCasual: pct2dec(f.minTgpCasualPct),
  minSgpCasual: pct2dec(f.minSgpCasualPct),
  minTgpContractor: pct2dec(f.minTgpContractorPct),
  minSgpContractor: pct2dec(f.minSgpContractorPct),
  defaultBillableDaysCasual: Number(f.defaultBillableDaysCasual),
})

const RateCalculatorSettings: React.FC<Props> = ({ embedded = false }) => {
  const [rows, setRows] = useState<TapRateCalculatorSettings[]>([])
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [form, setForm] = useState<FormState>(blankForm())
  const [creatingNew, setCreatingNew] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<Date | null>(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const list = await listRateCalculatorSettings()
      setRows(list)
      // Default selection: active row if any, else first row, else creating-new
      const active = list.find((r) => r.isActive)
      const initial = active || list[0] || null
      if (initial) {
        setSelectedId(initial.id)
        setForm(fromSettings(initial))
        setCreatingNew(false)
      } else {
        setSelectedId(null)
        setForm(blankForm())
        setCreatingNew(true)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load rate calculator settings')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
  }, [])

  const selected = rows.find((r) => r.id === selectedId) || null

  // Bug guard: capacity / utilisation are derived; recompute live so the
  // user sees the impact of their input edits before saving.
  const liveCapacityDays = (() => {
    const w = Number(form.weekdaysPerYear) || 0
    const off = (Number(form.annualLeaveDays) || 0) + (Number(form.sickLeaveDays) || 0) +
      (Number(form.professionalDevelopmentDays) || 0) + (Number(form.internalManagementDays) || 0)
    return Math.max(0, w - off)
  })()
  const liveUtilisationTargetDays = liveCapacityDays * (pct2dec(form.utilisationTargetPct) || 0)

  const handleSelectRow = (id: string) => {
    const row = rows.find((r) => r.id === id)
    if (!row) return
    setSelectedId(id)
    setForm(fromSettings(row))
    setCreatingNew(false)
    setSavedAt(null)
  }

  const handleNewRow = () => {
    setSelectedId(null)
    setForm(blankForm())
    setCreatingNew(true)
    setSavedAt(null)
  }

  const handleSave = async () => {
    if (!form.fyLabel.trim()) {
      setError('FY label is required.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      if (creatingNew || !selectedId) {
        const created = await createRateCalculatorSettings(toPayload(form))
        await load()
        setSelectedId(created.id)
        setCreatingNew(false)
      } else {
        await updateRateCalculatorSettings(selectedId, toPayload(form))
        await load()
      }
      setSavedAt(new Date())
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(typeof detail === 'string' ? detail : 'Failed to save')
    } finally {
      setSaving(false)
    }
  }

  const handleActivate = async () => {
    if (!selectedId) return
    if (selected?.isActive) return
    if (!confirm(`Activate "${selected?.fyLabel}" — this becomes the live record used by every Assessment tab.`)) return
    setSaving(true)
    try {
      await activateRateCalculatorSettings(selectedId)
      await load()
    } catch {
      alert('Failed to activate row')
    } finally {
      setSaving(false)
    }
  }

  // Tiny composable for a labelled numeric input — keeps the form JSX
  // from drowning in repeated boilerplate.
  const NumField: React.FC<{ label: string; value: string; onChange: (v: string) => void; suffix?: string; step?: string }> = ({ label, value, onChange, suffix, step }) => (
    <div>
      <label style={labelStyle}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          step={step || '0.01'}
          style={inputStyle}
        />
        {suffix && <span style={{ fontSize: '12px', color: 'var(--color-dark-grey)', whiteSpace: 'nowrap' }}>{suffix}</span>}
      </div>
    </div>
  )

  return (
    <div style={{ padding: '0' }}>
      {!embedded && (
        <PageHeader
          title="Rate Calculator"
          subtitle="Variables driving the live rate calculator on the Assessment tab"
        />
      )}

      <div style={{ padding: '16px 24px 24px 24px' }}>
        {error && (
          <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '6px', padding: '12px 16px', marginBottom: '16px', display: 'flex', gap: '10px' }}>
            <AlertCircle size={20} style={{ color: '#dc2626' }} />
            <div style={{ color: '#991b1b', fontSize: '14px' }}>{error}</div>
          </div>
        )}

        {loading ? (
          <div style={{ color: 'var(--color-dark-grey)', padding: '24px 0' }}>Loading…</div>
        ) : (
          <>
            {/* FY row picker */}
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap' }}>
              <label style={{ ...labelStyle, marginBottom: 0 }}>FY Settings:</label>
              <select
                value={creatingNew ? '__new__' : (selectedId || '')}
                onChange={(e) => {
                  if (e.target.value === '__new__') handleNewRow()
                  else handleSelectRow(e.target.value)
                }}
                style={{ ...inputStyle, width: 'auto', minWidth: '200px', cursor: 'pointer' }}
              >
                {rows.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.fyLabel}{r.isActive ? ' (active)' : ''}
                  </option>
                ))}
                <option value="__new__">+ New FY settings…</option>
              </select>
              {selected && !selected.isActive && (
                <button onClick={handleActivate} style={secondaryBtn} disabled={saving}>
                  Activate this FY
                </button>
              )}
              {selected?.isActive && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '13px', color: '#047857', fontWeight: 600 }}>
                  <CheckCircle size={14} /> Active
                </span>
              )}
            </div>

            {/* FY label + notes */}
            <div style={sectionStyle}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600 }}>Identity</h3>
              <div style={{ display: 'grid', gridTemplateColumns: '200px 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>FY Label</label>
                  <input type="text" value={form.fyLabel} onChange={(e) => setForm({ ...form, fyLabel: e.target.value })} placeholder="e.g. FY26" style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Notes (optional)</label>
                  <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="e.g. Effective 2025-07-01" style={inputStyle} />
                </div>
              </div>
            </div>

            {/* Global rates */}
            <div style={sectionStyle}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600 }}>Global Rates</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <NumField label="Superannuation rate" value={form.superRatePct} onChange={(v) => setForm({ ...form, superRatePct: v })} suffix="%" />
                <NumField label="Payroll tax rate" value={form.payrollTaxRatePct} onChange={(v) => setForm({ ...form, payrollTaxRatePct: v })} suffix="%" />
                <NumField label="Standard hours per day" value={form.hoursPerDay} onChange={(v) => setForm({ ...form, hoursPerDay: v })} suffix="hrs" />
              </div>
            </div>

            {/* Capacity / utilisation */}
            <div style={sectionStyle}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600 }}>Capacity / Utilisation (Permanent only)</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px' }}>
                <NumField label="Weekdays per year" value={form.weekdaysPerYear} onChange={(v) => setForm({ ...form, weekdaysPerYear: v })} step="1" />
                <NumField label="Annual leave (days)" value={form.annualLeaveDays} onChange={(v) => setForm({ ...form, annualLeaveDays: v })} step="1" />
                <NumField label="Sick leave (days)" value={form.sickLeaveDays} onChange={(v) => setForm({ ...form, sickLeaveDays: v })} step="1" />
                <NumField label="Professional development (days)" value={form.professionalDevelopmentDays} onChange={(v) => setForm({ ...form, professionalDevelopmentDays: v })} step="1" />
                <NumField label="Internal/management time (days)" value={form.internalManagementDays} onChange={(v) => setForm({ ...form, internalManagementDays: v })} step="1" />
                <NumField label="Utilisation target" value={form.utilisationTargetPct} onChange={(v) => setForm({ ...form, utilisationTargetPct: v })} suffix="%" />
              </div>
              <div style={{ marginTop: '12px', padding: '10px 14px', backgroundColor: '#f9fafb', borderRadius: '6px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>
                <strong>Derived:</strong>{' '}
                Capacity = <code>{liveCapacityDays}</code> days,{' '}
                Utilisation Target = <code>{liveUtilisationTargetDays.toFixed(1)}</code> days{' '}
                <em>(read-only — recomputed live as you change the inputs above)</em>
              </div>
            </div>

            {/* Margin matrix */}
            <div style={sectionStyle}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600 }}>Margin Matrix per Employment Type</h3>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                <thead>
                  <tr style={{ color: 'var(--color-dark-grey)' }}>
                    <th style={{ padding: '6px 12px', textAlign: 'left', fontSize: '12px', textTransform: 'uppercase' }}>Employment Type</th>
                    <th style={{ padding: '6px 12px', textAlign: 'left', fontSize: '12px', textTransform: 'uppercase' }}>Min TGP</th>
                    <th style={{ padding: '6px 12px', textAlign: 'left', fontSize: '12px', textTransform: 'uppercase' }}>Min SGP</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: 'Full Time', tgpKey: 'minTgpFullTimePct' as const, sgpKey: 'minSgpFullTimePct' as const },
                    { label: 'Part Time', tgpKey: 'minTgpPartTimePct' as const, sgpKey: 'minSgpPartTimePct' as const },
                    { label: 'Casual', tgpKey: 'minTgpCasualPct' as const, sgpKey: 'minSgpCasualPct' as const },
                    { label: 'Contractor', tgpKey: 'minTgpContractorPct' as const, sgpKey: 'minSgpContractorPct' as const },
                  ].map((row) => (
                    <tr key={row.label}>
                      <td style={{ padding: '6px 12px', fontWeight: 500 }}>{row.label}</td>
                      <td style={{ padding: '6px 12px' }}>
                        <input type="number" step="0.01" value={form[row.tgpKey]} onChange={(e) => setForm({ ...form, [row.tgpKey]: e.target.value })} style={{ ...inputStyle, width: '100px' }} /> %
                      </td>
                      <td style={{ padding: '6px 12px' }}>
                        <input type="number" step="0.01" value={form[row.sgpKey]} onChange={(e) => setForm({ ...form, [row.sgpKey]: e.target.value })} style={{ ...inputStyle, width: '100px' }} /> %
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Form defaults */}
            <div style={sectionStyle}>
              <h3 style={{ margin: '0 0 12px 0', fontSize: '15px', fontWeight: 600 }}>Form Defaults</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
                <NumField label="Default Hrs Per Day" value={form.hoursPerDay} onChange={(v) => setForm({ ...form, hoursPerDay: v })} suffix="hrs" />
                <NumField label="Default Billable Days (casual)" value={form.defaultBillableDaysCasual} onChange={(v) => setForm({ ...form, defaultBillableDaysCasual: v })} step="1" />
              </div>
              <div style={{ marginTop: '8px', fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                FTE Load is fixed at 1.0 in the calculator — Part Time salaries are entered as the actual offer figure (no FTE factor).
              </div>
            </div>

            {/* Audit footer */}
            {selected && !creatingNew && (
              <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginBottom: '12px' }}>
                Last updated: {formatDateTime(selected.updatedAt)}
                {selected.updatedBy ? ` by ${selected.updatedBy}` : ''}
                {' · '}Created: {formatDateTime(selected.createdAt)}
              </div>
            )}

            {/* Action bar */}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center' }}>
              {savedAt && (
                <span style={{ fontSize: '12px', color: '#047857', marginRight: '8px' }}>
                  Saved {formatDateTime(savedAt.toISOString())}
                </span>
              )}
              <button onClick={handleSave} style={primaryBtn} disabled={saving}>
                {saving ? 'Saving…' : creatingNew ? 'Create FY settings' : 'Save changes'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default RateCalculatorSettings
